/**
 * Portal Impersonations — which of our employees was in which patient's
 * account, in one clinic, when, for how long, and why.
 *
 * Spec: `.spec/portal-impersonation-clinic-local.md` § D2, superseding
 * `.spec/portal-impersonation.md` § Part D.
 *
 * ## One clinic at a time, and the empty state that comes with it
 *
 * The log lives in each clinic's own database now, so this page begins by
 * asking which clinic. Until one is picked there is nothing to show and the
 * page says so, rather than showing an empty table that reads as "nobody has
 * impersonated anyone".
 *
 * **No fan-out, at any point.** Rendering "the fleet" by asking N clinics in a
 * loop is the cost and the cross-tenant read the move removed, and putting the
 * loop in the browser does not make it a different loop. Fleet-wide,
 * actor-first search ("everything this employee touched" when the clinics are
 * unknown) is a known, accepted gap — if it comes back it wants a deliberate
 * cross-tenant reporting surface, not this page in a `for`.
 *
 * Every count on this page is therefore SCOPED to the selected clinic, and the
 * labels say so. A count that reads as fleet-wide and is not is worse than no
 * count at all, especially the live one.
 *
 * NOT temporary, unlike `PortalAuthPage`. The migration view dies with
 * Firebase; this log outlives it. What the migration changes is the log's
 * COVERAGE: core only records an impersonation for a clinic whose portal runs
 * core auth, so the table covers flipped clinics today and the whole fleet at
 * teardown. That is stated on the page, because an empty table has two readings
 * and only one of them is good news.
 *
 * ## History is the record; "in right now" is a filter on it
 *
 * There is one dataset and one table. The "In right now" filter is three
 * conditions on the same rows — consumed, not ended, not expired — rather than
 * a second view over a second store. A live panel that could disagree with the
 * history under it is the thing somebody would be staring at during an
 * incident.
 *
 * ## This page names patients, and that is the point
 *
 * `PortalAuthPage` carries no patient data at all, not even masked, and that
 * rule is unchanged there. Here a row shows a masked address and a
 * `customer_id`, because a log that will not say whose account was entered
 * answers nothing. The address is masked at the source: core writes only
 * `email_masked` into the collection, so this page could not unmask it.
 *
 * ## Vocabulary
 *
 * "Portal impersonation" throughout. core-management ALREADY has a super_admin
 * impersonating another core-management *user* (`utils/jwt.js`,
 * `middleware/auth.js`) — an unrelated system in the same codebase. Neither may
 * borrow the other's names.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshCw, Loader2, Info, Building2 } from 'lucide-react'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge, Input,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui'
import { portalImpersonationsAPI } from '../services/api'

/**
 * The outcome vocabulary, from core's `constants.py` via
 * `portalImpersonations.js`. The backend returns ids and no sentences — a
 * reason id is what a test asserts, a sentence is what a designer changes — so
 * this table is the only place the wording lives.
 *
 * The two labels that carry the whole distinction:
 *   `expired_unused` — a link that was minted and never opened. **Not an
 *                      impersonation.** Labelling it "expired" alongside the
 *                      other kind would make every abandoned click read as
 *                      account access.
 *   `expired`        — an employee who walked away, as against `ended`, an
 *                      employee who finished. Different things to follow up.
 */
const OUTCOME_LABEL = {
  issued: 'Link issued',
  consumed: 'In progress',
  ended: 'Ended',
  expired: 'Expired (left open)',
  expired_unused: 'Link never opened',
  failed: 'Refused',
}

const OUTCOME_PILL = {
  issued: 'outline',
  consumed: 'success',
  ended: 'secondary',
  expired: 'warning',
  expired_unused: 'gray',
  failed: 'destructive',
}

/** Core's refusal codes, in words. Must stay total against `constants.py`. */
const FAILURE_LABEL = {
  IMPERSONATION_NO_IDENTITY: 'Patient had no portal identity',
  IMPERSONATION_NO_EMAIL: 'Patient has no email address',
  IMPERSONATION_NOT_PROVISIONED: 'Clinic not provisioned for portal auth',
  IMPERSONATION_EMAIL_COLLISION: 'Two patients share this email address',
  IMPERSONATION_NO_REASON: 'No reason supplied',
}

/** Core's reason picklist, in words. */
const REASON_LABEL = {
  support_request: 'Support request',
  billing_issue: 'Billing issue',
  order_issue: 'Order issue',
  technical_issue: 'Technical issue',
  clinical_review: 'Clinical review',
  qa_verification: 'QA / verification',
}

const FILTERS = ['All', 'In right now', 'Completed', 'Never opened', 'Refused']

/** The error code the backend sends when no clinic was picked. */
const CLINIC_REQUIRED = 'CLINIC_REQUIRED'

function formatUtc(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC')
}

/** `null` renders as an em dash, never as 0 — "not applicable" is not "none". */
function duration(seconds) {
  if (!Number.isFinite(seconds)) return '—'
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message || fallback
}

/**
 * The filter chips, mapped to query parameters.
 *
 * "In right now" is `live=true`, which the backend turns into the three
 * conditions. It is deliberately NOT `outcome=consumed`: core moves a lapsed
 * document to `expired` lazily, so a `consumed` row can be an hour stale and
 * would report somebody as inside an account they were ejected from.
 */
function queryFor(filter) {
  switch (filter) {
    case 'In right now':
      return { live: true }
    case 'Completed':
      return { outcome: 'ended' }
    case 'Never opened':
      return { outcome: 'expired_unused' }
    case 'Refused':
      return { outcome: 'failed' }
    default:
      return {}
  }
}

/**
 * The roll-up, scoped to the selected clinic — which is why every label says
 * "in this clinic". "In right now" reading as a fleet-wide count when it is one
 * clinic's is the specific mistake worth the extra four words: it is the number
 * somebody reads during an incident.
 *
 * The old "Clinics" tile is gone rather than relabelled. In a per-clinic log it
 * is always 1, so it carried no information and read as a fleet count. Core
 * still returns it — the wire shape did not change with the move — and this page
 * simply does not render it.
 */
function Rollup({ summary, loading }) {
  const tiles = [
    { label: 'In this clinic now', value: summary?.live ?? 0, emphasis: (summary?.live ?? 0) > 0 },
    { label: 'Impersonations', value: summary?.matched ?? 0 },
    { label: 'Employees', value: summary?.actors ?? 0 },
    { label: 'Patients', value: summary?.subjects ?? 0 },
    { label: 'Refused', value: summary?.byOutcome?.failed ?? 0 },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {tiles.map((tile) => (
        <Card key={tile.label}>
          <CardContent className="py-4">
            <p className="text-xs text-muted-foreground">{tile.label}</p>
            <p className={`text-2xl font-semibold ${tile.emphasis ? 'text-green-600' : ''}`}>
              {loading ? '…' : tile.value.toLocaleString()}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export default function PortalImpersonationsPage() {
  const [clinics, setClinics] = useState([])
  const [clinicsError, setClinicsError] = useState(null)
  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState(null)
  const [refreshedAt, setRefreshedAt] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [filter, setFilter] = useState('All')
  const [actorEmail, setActorEmail] = useState('')
  // No default and no "first clinic in the list". A page that lands already
  // showing someone's impersonations invites reading them as the fleet's.
  const [clinic, setClinic] = useState('')

  // The picker's options. One request, on mount, independent of the log — so a
  // clinic list that fails leaves a page that says why rather than a picker
  // that is silently empty.
  useEffect(() => {
    let cancelled = false
    portalImpersonationsAPI
      .clinics()
      .then((response) => {
        if (!cancelled) setClinics(response.data.data.clinics || [])
      })
      .catch((err) => {
        if (!cancelled) setClinicsError(apiErrorMessage(err, 'Unable to load the clinic list'))
      })
    return () => {
      cancelled = true
    }
  }, [])

  const load = useCallback(async (bypassCache) => {
    // The guard that keeps the empty state honest. One clinic or no request —
    // never a loop over the fleet, here or anywhere else on this page.
    if (!clinic) {
      setRows([])
      setSummary(null)
      setRefreshedAt(null)
      setLoadError(null)
      return
    }

    setLoading(true)
    setLoadError(null)
    try {
      const response = await portalImpersonationsAPI.list({
        ...queryFor(filter),
        ...(actorEmail.trim() ? { actorEmail: actorEmail.trim() } : {}),
        clinic,
        ...(bypassCache ? { bypassCache: true } : {}),
      })
      const data = response.data.data
      setRows(data.rows)
      setSummary(data.summary)
      setRefreshedAt(data.refreshedAt)
    } catch (err) {
      // A CLINIC_REQUIRED here means the guard above was bypassed, not that the
      // operator did something wrong; it still resolves to "pick a clinic".
      const code = err?.response?.data?.error?.code
      setLoadError(
        code === CLINIC_REQUIRED
          ? 'Pick a clinic to read its impersonation log'
          : apiErrorMessage(err, 'Unable to load the impersonation log'),
      )
    } finally {
      setLoading(false)
    }
  }, [actorEmail, clinic, filter])

  useEffect(() => {
    load(false)
  }, [load])

  const truncated = useMemo(
    () => Boolean(summary && summary.matched > rows.length),
    [summary, rows.length],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Portal Impersonations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Which employee was in which patient’s portal account, in one clinic, and why.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/*
            The clinic picker, in the header rather than beside the filters,
            because it is not one: it chooses which log is open. The free-text
            Clinic filter it replaces used to narrow a fleet-wide table.
          */}
          <Select value={clinic || undefined} onValueChange={setClinic}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Choose a clinic…" />
            </SelectTrigger>
            <SelectContent>
              {clinics.map((option) => (
                <SelectItem key={option.name} value={option.name}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {refreshedAt && (
            <span className="text-xs text-muted-foreground">Last refreshed: {formatUtc(refreshedAt)}</span>
          )}
          <Button variant="outline" onClick={() => load(true)} disabled={loading || !clinic}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {clinicsError && <p className="text-sm text-destructive">{clinicsError}</p>}

      {/*
        The coverage caveat, on the page rather than in a doc. An empty table
        has two readings — "nobody has impersonated anyone" and "none of these
        clinics is flipped yet" — and only one of them is good news.
      */}
      <div className="flex items-start gap-2 rounded-md border border-blue-500/40 bg-blue-500/10 p-3 text-sm">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
        <p>
          <span className="font-medium">Core-auth clinics only.</span>{' '}
          A clinic still on Firebase auth keeps its old impersonation flow, which records nothing
          anywhere. Coverage grows as clinics flip and is complete when Firebase is turned off —
          so an empty result here may mean no impersonations, or no flipped clinics.
        </p>
      </div>

      {/*
        The empty state, and the page stops here.

        A table of zero rows would read as "nobody has impersonated anyone in
        this clinic", which is a claim this page has not checked. Saying nothing
        until a clinic is chosen is the honest version.
      */}
      {!clinic ? (
        <Card>
          <CardContent className="py-12 flex flex-col items-center text-center gap-2">
            <Building2 className="h-8 w-8 text-muted-foreground" />
            <p className="font-medium">Choose a clinic</p>
            <p className="text-sm text-muted-foreground max-w-md">
              Each clinic’s impersonation log lives in that clinic’s own database, so this page
              reads one clinic at a time. There is no fleet-wide view — “everything this employee
              touched” across clinics is not a question this page answers.
            </p>
          </CardContent>
        </Card>
      ) : (
      <>
      <Rollup summary={summary} loading={loading} />

      <Card>
        <CardHeader>
          <CardTitle>Impersonations in {clinic}</CardTitle>
          <CardDescription>
            Last 30 days unless filtered. “In right now” is a filter on this history, not a
            separate list: consumed, not ended, not expired. Every count on this page is this
            clinic’s.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((option) => (
              <Button
                key={option}
                size="sm"
                variant={filter === option ? 'default' : 'outline'}
                onClick={() => setFilter(option)}
              >
                {option}
              </Button>
            ))}
            {/*
              The Clinic text box that used to sit here is the picker in the
              header now. It narrowed a fleet-wide table; there is no longer a
              fleet-wide table to narrow.
            */}
            <Input
              value={actorEmail}
              onChange={(event) => setActorEmail(event.target.value)}
              placeholder="Employee email…"
              className="h-8 w-56 ml-auto"
            />
          </div>

          {loadError && <p className="text-sm text-destructive">{loadError}</p>}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2 font-medium">Employee</th>
                  <th className="text-left p-2 font-medium">Patient</th>
                  <th className="text-left p-2 font-medium">Clinic</th>
                  <th className="text-left p-2 font-medium">Reason</th>
                  <th className="text-left p-2 font-medium">Issued</th>
                  <th className="text-left p-2 font-medium">Entered</th>
                  <th className="text-right p-2 font-medium">Duration</th>
                  <th className="text-left p-2 font-medium">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t align-top">
                    <td className="p-2">
                      <span className="font-medium">{row.actor.email || '—'}</span>
                      {row.actor.name && (
                        <p className="text-xs text-muted-foreground">{row.actor.name}</p>
                      )}
                    </td>
                    <td className="p-2">
                      {/*
                        Masked address plus customer_id. The fleet page's
                        counts-only rule does not apply here: naming the account
                        is the point of an impersonation log.
                      */}
                      <span>{row.subject.emailMasked || '—'}</span>
                      {row.subject.customerId && (
                        <p className="text-xs text-muted-foreground font-mono">{row.subject.customerId}</p>
                      )}
                    </td>
                    <td className="p-2">{row.clinic || '—'}</td>
                    <td className="p-2">
                      <span>{REASON_LABEL[row.reason.code] || row.reason.code || '—'}</span>
                      {row.reason.note && (
                        <p className="text-xs text-muted-foreground">{row.reason.note}</p>
                      )}
                    </td>
                    <td className="p-2 whitespace-nowrap text-xs">{formatUtc(row.issuedAt) || '—'}</td>
                    <td className="p-2 whitespace-nowrap text-xs">{formatUtc(row.consumedAt) || '—'}</td>
                    <td className="p-2 text-right">{duration(row.durationSeconds)}</td>
                    <td className="p-2">
                      <Badge variant={OUTCOME_PILL[row.outcome] || 'gray'}>
                        {OUTCOME_LABEL[row.outcome] || row.outcome}
                      </Badge>
                      {row.live && (
                        <p className="text-xs text-green-600 mt-1">In this account now</p>
                      )}
                      {row.failureCode && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {FAILURE_LABEL[row.failureCode] || row.failureCode}
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-muted-foreground">
                      No impersonations in {clinic} match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {truncated && (
            <p className="text-xs text-muted-foreground">
              Showing {rows.length} of {summary.matched.toLocaleString()} — narrow the filter to see
              the rest.
            </p>
          )}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  )
}
